<template>
  <div class="generic-select__component">
    <GenericDropdownContainer
      :id="`select-${id}`"
      ref="dropdownContainer"
      :input-name="name"
      type="input"
      :selected-options="[selectedOption]"
      :custom-selected-label="getSelectedLabel"
      :input-label="label"
      :required="required"
      :disabled="disabled || readonly"
      :readonly="readonly"
      :invalid="invalid"
      :has-description="hasDescription"
      :description-id="ariaDescriptionIds"
      :has-errors="materialHasErrors"
      :placeholder="placeholder"
      :default-value="getDefaultValue"
      @handle-validation="handleValidation"
      @handleInput="handleOnChange"
    >
      <!-- eslint-disable-next-line vuejs-accessibility/role-has-required-aria-props -->
      <div
        :id="`generic-select__option-id--null-${id}`"
        class="generic-select__option-item"
        role="option"
        :value="null"
        :name="null"
      >
        <div class="generic-select__option-text"></div>
      </div>
      <div
        v-for="(option, key) in options"
        :id="`generic-select__option-id--${keyValue(key)}`"
        :key="key"
        :value="keyValue(key)"
        :name="key"
        class="generic-select__option-item"
        :class="{ '-selected': key === selectedOption }"
        role="option"
        :aria-selected="key === selectedOption"
      >
        <img
          v-if="extractOptionData(option, 'icon').length"
          class="generic-select__option-image"
          :src="extractOptionData(option, 'icon')"
          alt=""
        />
        <div class="generic-select__option-text">
          {{ extractOptionData(option, 'label') }}
        </div>
      </div>
    </GenericDropdownContainer>

    <div v-show="hasDescription" :id="`description-${id}`">
      <div class="generic-errors">
        <div
          v-for="(error, key) in computedErrors"
          :key="key"
          class="generic-errors__item"
        >
          {{ error }}
        </div>
      </div>
      <slot name="helperText"></slot>
    </div>
  </div>
</template>

<script>
import GenericDropdownContainer from './generic-dropdown-container.vue';

export default {
  name: 'GenericSelect',
  components: {
    GenericDropdownContainer,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [Array, String],
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    value: {
      type: String,
      default: null,
    },
    defaultValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedOption: this.value,
      invalid: false,
    };
  },
  computed: {
    ariaDescriptionIds() {
      return this.hasDescription ? `description-${this.id}` : null;
    },
    hasDescription() {
      return this.errorMessages || this.$slots.helperText;
    },
    materialHasErrors() {
      return this.invalid || this.hasErrorMessages;
    },
    hasErrorMessages() {
      return this.errorMessages !== null && this.errorMessages.length > 0;
    },
    computedErrors() {
      return typeof this.errorMessages === 'string'
        ? [this.errorMessages]
        : this.errorMessages;
    },
    activeOptionLabel() {
      const keySelected = this.selectedOption.replace(/[^a-zA-Z]/g, '');
      const keySelectedString = keySelected.replace(/ /g, '');
      const activeOption = document.querySelector(
        `div#generic-select__option-id--${keySelectedString}`
      );
      return activeOption?.innerText;
    },
    activeOptionImage() {
      const keySelected = this.selectedOption.replace(/[^a-zA-Z]/g, '');
      const keySelectedString = keySelected.replace(/ /g, '');
      const activeOption = document.querySelector(
        `div#generic-select__option-id--${keySelectedString}`
      );
      const activeImage = activeOption?.querySelector('img');
      return activeImage?.getAttribute('src');
    },
    getSelectedLabel() {
      return this.selectedOption
        ? this.activeOptionImage
          ? `<img
        class="generic-select__selected-image"
        src="${this.activeOptionImage}"
      /><span class="generic-select__selected-label">${this.activeOptionLabel}</span>`
          : `<span class="generic-select__selected-label">${this.activeOptionLabel}</span>`
        : null;
    },
    getDefaultValue() {
      return this.selectedOption
        ? this.extractOptionData(this.options[this.selectedOption], 'label')
        : this.extractOptionData(this.options[this.defaultValue], 'label');
    },
  },
  mounted() {
    this.handleDefaultValue();
  },
  methods: {
    triggerHideList(event) {
      const dropdownContainer = this.$refs.dropdownContainer;
      dropdownContainer.collapseFromListbox(event);
    },
    setGridStyle() {
      const dropdownContainer = this.$refs.dropdownContainer;
      dropdownContainer.setGridStyle();
    },
    handleOnChange(event, isKeyboardEvent) {
      if (isKeyboardEvent) {
        const listbox = event.target.parentElement.querySelector(
          '.generic-select__listbox'
        );
        const focusedOption = listbox.querySelector('.-focused');
        this.selectedOption = focusedOption.getAttribute('name');
      } else {
        this.selectedOption = event.target.getAttribute('name');
      }
      this.$emit('updated', {
        fieldName: this.id,
        value: this.selectedOption,
      });
      this.$emit('input');
      this.handleValidation();
      this.triggerHideList(event);
      this.setGridStyle();
    },
    handleValidation() {
      this.invalid = this.required && !this.selectedOption;
    },
    handleDefaultValue() {
      if (!this.selectedOption && this.defaultValue)
        this.selectedOption = this.defaultValue;
    },
    extractOptionData(option, key) {
      if (typeof option === 'object') return option[key];
      if (this.isValidJson(option)) {
        const optionItem = JSON.parse(option);
        if (optionItem[key].length) return optionItem[key];
        return '';
      } else {
        if (key === 'label') return option;
        return '';
      }
    },
    isValidJson(string) {
      if (typeof string !== 'string') {
        return false;
      }
      try {
        JSON.parse(string);
        return true;
      } catch (error) {
        return false;
      }
    },
    keyValue(key) {
      const keySelected = key.replace(/[^a-zA-Z]/g, '');
      return keySelected.replace(/ /g, '');
    },
  },
};
</script>
